import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, Row, Col, Card, Popover } from 'antd';
import SubnavBar from '../subnav_bar/SubnavBar';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	changeFilterValueToArrayOfIds,
	getStartDateFieldName,
	getEndDateFieldName,
	getEntitiesByIdWithSearch,
} from '../../utils/DataAccessUtils';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import { workOrderPrioritiesRestCrudThunksForSupplier } from '../../thunks/work_order_priorities_thunks';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import EventCalendar from '../event_calendar/EventCalendar';
import {
	capitalize,
	formatDateForParam,
	encodeUTCAdjustedDate,
} from '../../utils/DataFormatterUtils';
import moment from 'moment';
import { ServiceCallPopoverDisplayV2 } from '../service_call_popover_display/ServiceCallPopoverDisplayV2';
import OWAsyncTreeSelect from '../ow_async_tree_select/OWAsyncTreeSelect';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import SupplierRegionsLocationsTreeSelect from '../regions_tree_select/SupplierRegionsLocationsTreeSelect';
import {
	fetchWorkOrders,
	updateFiltersWorkOrders,
	clearFiltersWorkOrders,
} from '../../thunks/work_orders_thunks_wrapper';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { Views } from 'react-big-calendar';

const { Content } = Layout;
const WORK_ORDERS_CALENDAR_TARGET_COLLECTION = 'workOrdersCalendarIndex';
const LAST_SERVICE_CALL_SCHEDULED_AT_FIELD = FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT;

require('./SupplierServiceCallCalendarPage.less');

const SupplierServiceCallCalendarPageV2 = (props: any) => {
	const {
		workOrders,
		data,
		fetchData,
		updateFilters,
		clearFilters,
		fetchLocations,
		fetchMultipleLocations,
		fetchWorkOrderPriorities,
		fetchMultipleWorkOrderPriorities,
		fetchMultipleFieldTechs,
		fetchFieldTechs,
		locations,
		workOrderPriorities,
		fieldTechs,
		history,
		location,
	} = props;

	useEffect(() => {
		fetchData(location);
	}, []);

	const handleClearAndUpdateFilters = () => {
		const startDateFieldName = getStartDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);
		const endDateFieldName = getEndDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);

		const startDate = nullSafeGet(`filters.${startDateFieldName}`, workOrders);
		const endDate = nullSafeGet(`filters.${endDateFieldName}`, workOrders);

		clearFilters({
			[startDateFieldName]: startDate,
			[endDateFieldName]: endDate,
		});
	};

	const handleSelectEvent = (event) => {
		const workOrderId = nullSafeGet('workOrderId', event) || nullSafeGet('id', event);
		if (workOrderId) {
			history.push(
				getLinkWIthBackLinkParams(
					location,
					'Back to Service Schedule',
					`/supplier/workOrders/detail/${workOrderId}/details`
				)
			);
		}
	};

	const handleRangeChange = (range) => {
		const startDateFieldName = getStartDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);
		const endDateFieldName = getEndDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);

		let startDate, endDate;

		if (Array.isArray(range)) {
			if (range.length === 1) {
				startDate = formatDateForParam(encodeUTCAdjustedDate(moment(range[0]).subtract(1, 'day')));
				endDate = formatDateForParam(encodeUTCAdjustedDate(moment(range[0]).add(1, 'day'), true));
			} else {
				startDate = formatDateForParam(encodeUTCAdjustedDate(moment(range[0])));
				endDate = formatDateForParam(encodeUTCAdjustedDate(moment(range[range.length - 1]), true));
			}
		} else if (range.start && range.end) {
			startDate = formatDateForParam(encodeUTCAdjustedDate(moment(range.start)));
			endDate = formatDateForParam(encodeUTCAdjustedDate(moment(range.end), true));
		}

		if (startDate && endDate) {
			updateFilters({
				[startDateFieldName]: startDate,
				[endDateFieldName]: endDate,
			});
		}
	};

	const transformRecords = useMemo(() => {
		if (!data || data.length === 0) {
			return [];
		}
		return data.reduce((acc, workOrder) => {
			const lastServiceCall = nullSafeGet('lastServiceCall', workOrder);
			const serviceScheduledAt = nullSafeGet('serviceScheduledAt', lastServiceCall);

			if (!serviceScheduledAt) {
				return acc;
			}

			const locationName = nullSafeGet('location.name', workOrder);
			const supplierFacilityName = nullSafeGet('supplierFacility.name', workOrder);
			const fieldTechName = nullSafeGet('fieldTechnician.name', lastServiceCall);

			let title = `#${workOrder.id} - ${capitalize(
				nullSafeGetOrElse('title', workOrder, 'Scheduled')
			)}`;
			if (locationName) {
				title += ` at ${locationName}`;
			}
			if (supplierFacilityName) {
				title += ` (${supplierFacilityName})`;
			}
			if (fieldTechName) {
				title += ` - ${fieldTechName}`;
			}

			// Use scheduled time at start of hour
			const scheduledDate = moment(serviceScheduledAt).startOf('hour');

			const event = {
				...workOrder,
				title,
				category: nullSafeGet('displayStatus', workOrder),
				start: scheduledDate.toDate(),
				end: scheduledDate.clone().add(1, 'hour').toDate(),
			};

			return acc.concat(event);
		}, []);
	}, [data]);

	const EventComponent = ({ event }) => {
		return (
			<Popover
				trigger="hover"
				placement="topLeft"
				overlayClassName="workOrderPopoverDisplay_wrapper"
				content={<ServiceCallPopoverDisplayV2 workOrder={event} />}
			>
				{event && event.title}
			</Popover>
		);
	};

	const filterConfig = [
		{
			label: 'Location',
			stateSlice: locations,
			fieldName: FILTER_CONFIG_NAMES.LOCATION,
			mode: 'multiple',
			filtersValueAccessor: (filtersStateSlice) => {
				const ids = nullSafeGet(`filters.locationIds`, filtersStateSlice);
				return changeFilterValueToArrayOfIds(ids);
			},
			keyAccessor: (loc) => loc.id,
			valueAccessor: (loc) => loc.id,
			handleChange: (ids) => {
				updateFilters({ locationIds: ids.join(',') || undefined });
			},
			targetCollectionName: 'workOrdersAdvancedFilters',
			fetch: fetchLocations,
			fetchMultiple: fetchMultipleLocations,
			renderItem: (loc) => `${nullSafeGetOrElse('buyerCompany.name', loc, '')} - ${loc.name}`,
			sortBy: { sort_by: 'name', order: 'ascend' },
			component: SupplierRegionsLocationsTreeSelect,
		},
		{
			label: 'Technician',
			fieldName: FILTER_CONFIG_NAMES.TECHNICIAN,
			stateSlice: fieldTechs,
			filtersValueAccessor: (filtersStateSlice) =>
				nullSafeGet(`filters.fieldTechnicianEmail`, filtersStateSlice),
			keyAccessor: (fieldTech) => fieldTech.email,
			valueAccessor: (fieldTech) => fieldTech.email,
			handleChange: (technicianEmail) => {
				updateFilters({ fieldTechnicianEmail: technicianEmail });
			},
			targetCollectionName: 'workOrdersAdvancedFilters',
			fetch: fetchFieldTechs,
			fetchMultiple: fetchMultipleFieldTechs,
			renderItem: (fieldTech) => fieldTech.name,
			sortBy: { sort_by: 'name', order: 'ascend' },
		},
		{
			label: 'Priority',
			fieldName: FILTER_CONFIG_NAMES.PRIORITY,
			mode: 'multiple',
			stateSlice: workOrderPriorities,
			filtersValueAccessor: (filtersStateSlice) => {
				const woPriorityIds = nullSafeGet(`filters.woPriorityIds`, filtersStateSlice);
				return changeFilterValueToArrayOfIds(woPriorityIds);
			},
			keyAccessor: (workOrderPriority) => workOrderPriority.id,
			valueAccessor: (workOrderPriority) => workOrderPriority.id,
			handleChange: (ids) => {
				updateFilters({
					woPriorityIds: ids.join(',') || undefined,
				});
			},
			targetCollectionName: 'workOrdersAdvancedFilters',
			fetch: fetchWorkOrderPriorities,
			fetchMultiple: fetchMultipleWorkOrderPriorities,
			renderItem: (workOrderPriority) => workOrderPriority.name,
			component: OWAsyncTreeSelect,
		},
	];

	return (
		<Content className="supplierWorkOrdersIndexPage" style={{ padding: '0 0.5em' }}>
			<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<SubnavBar
						left={
							<div>
								<AdvancedFilters
									updateFilters={updateFilters}
									filterConfig={filterConfig}
									filtersTargetCollectionName={WORK_ORDERS_CALENDAR_TARGET_COLLECTION}
									clearAndUpdateFilters={handleClearAndUpdateFilters}
									filtersStateSlice={workOrders}
									preAppliedFilters={{}}
								/>
							</div>
						}
						right={<div></div>}
					/>
				</Col>
			</Row>
			<div className="flex w-full flex-1 flex-row">
				<SelectedFilters
					updateFilters={updateFilters}
					filterValues={nullSafeGetOrElse(`filters`, workOrders, {})}
					filterConfig={filterConfig}
				/>
			</div>
			<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<Card style={{ minHeight: 560 }}>
						<EventCalendar
							events={transformRecords}
							defaultDate={new Date()}
							defaultView={Views.WEEK}
							onRangeChange={handleRangeChange}
							onSelectEvent={handleSelectEvent}
							views={{
								week: true,
								day: true,
							}}
							components={{
								event: EventComponent,
							}}
						/>
					</Card>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	location: ownProps.location,
	workOrders: state.work_orders[WORK_ORDERS_CALENDAR_TARGET_COLLECTION],
	data: getEntitiesByIdWithSearch(
		state.work_orders[WORK_ORDERS_CALENDAR_TARGET_COLLECTION]
			? state.work_orders[WORK_ORDERS_CALENDAR_TARGET_COLLECTION].recordsIds || []
			: [],
		state.work_orders
	),
	locations: state.locations,
	workOrderPriorities: state.work_order_priorities,
	fieldTechs: state.field_techs,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const startDateFieldName = getStartDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);
	const endDateFieldName = getEndDateFieldName(LAST_SERVICE_CALL_SCHEDULED_AT_FIELD);

	// Calculate initial date range with proper formatting
	const startDate = formatDateForParam(encodeUTCAdjustedDate(moment().startOf('week')));
	const endDate = formatDateForParam(encodeUTCAdjustedDate(moment().endOf('week'), true));

	return {
		fetchData: (location) =>
			dispatch(
				fetchWorkOrders(ROLE_TYPES.SUPPLIER)(
					true, // handleQueryParams
					ownProps.history,
					location,
					WORK_ORDERS_CALENDAR_TARGET_COLLECTION,
					{
						[startDateFieldName]: startDate,
						[endDateFieldName]: endDate,
					},
					ownProps.initialPagination,
					ownProps.initialSorting,
					ownProps.initialFilters,
					true
				)
			),
		updateFilters: (filters) =>
			dispatch(
				updateFiltersWorkOrders(ROLE_TYPES.SUPPLIER)(
					true, // handleQueryParams
					ownProps.history,
					WORK_ORDERS_CALENDAR_TARGET_COLLECTION,
					filters,
					true // ignoreRefreshCounts
				)
			),
		clearFilters: (preserveFilters) =>
			dispatch(
				clearFiltersWorkOrders(ROLE_TYPES.SUPPLIER)(
					true, // handleQueryParams
					ownProps.history,
					WORK_ORDERS_CALENDAR_TARGET_COLLECTION,
					preserveFilters,
					true // ignoreRefreshCounts
				)
			),
		fetchMultipleWorkOrderPriorities: (ids, targetCollectionName) =>
			dispatch(
				workOrderPrioritiesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
			),
		fetchWorkOrderPriorities: (
			params,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				workOrderPrioritiesRestCrudThunksForSupplier.read(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
		fetchMultipleLocations: (ids, targetCollectionName) =>
			dispatch(locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
		fetchLocations: (
			params,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				locationsRestCrudThunksForSupplier.readLite(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
		fetchMultipleFieldTechs: (ids, targetCollectionName) =>
			dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
		fetchFieldTechs: (
			params,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				fieldTechsRestCrudThunksForSupplier.read(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
	};
};

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierServiceCallCalendarPageV2 as any)
);
